import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import { projectsApi } from "@/api/routes/projects"
import { Tag as SharedTag } from "@/api/tags"
import type { PlanMigrationStatus } from "@/billing/billing"
import type { BinaryType, Build, CloudProvider, RegionName } from "@/infra"
import type { DeployDomainKind, DeployDomainRef, UsageSummary } from "@/types"
import type { Workflow } from "@/workflows"

// ==========================================================================
// Types — Support Tickets
// ==========================================================================

export type SupportTicketStatus =
  | "new"
  | "on_you"
  | "on_customer"
  | "on_hold"
  | "closed"

export type SupportMessageSource = "email" | "discord" | "web" | "system"

export type TicketEventKind =
  | "status_changed"
  | "priority_changed"
  | "assigned"
  | "unassigned"

export interface AssigneeBrief {
  id: string
  username: string
  name: string
  gravatar_hash: string | null
}

export interface TicketSummary {
  id: string
  ticket_number: number
  subject: string
  status: SupportTicketStatus
  priority: number
  customer_email: string
  customer_user_id: string | null
  customer_username: string | null
  assignees: AssigneeBrief[]
  created_at: string
  updated_at: string
  last_customer_reply_at: string | null
  last_agent_reply_at: string | null
}

export interface TicketListResponse {
  tickets: TicketSummary[]
  total: number
  status_counts: Record<string, number>
}

export interface TicketDetail {
  id: string
  ticket_number: number
  subject: string
  status: SupportTicketStatus
  priority: number
  customer_email: string
  customer_user_id: string | null
  customer_username: string | null
  project_id: string | null
  discord_thread_id: number | null
  created_at: string
  updated_at: string
  closed_at: string | null
  last_customer_reply_at: string | null
  last_agent_reply_at: string | null
}

export interface MessageItem {
  id: string
  source: SupportMessageSource
  author_email: string | null
  author_user_id: string | null
  author_name: string | null
  body: string
  internal: boolean
  created_at: string
}

export interface EventItem {
  id: string
  kind: TicketEventKind
  actor_user_id: string | null
  actor_name: string
  old_status: SupportTicketStatus | null
  new_status: SupportTicketStatus | null
  old_priority: number | null
  new_priority: number | null
  target_user_id: string | null
  target_user_name: string | null
  created_at: string
}

export interface AssigneeItem {
  id: string
  username: string
  name: string
  email: string
  gravatar_hash: string | null
  assigned_at: string
}

export interface TicketDetailResponse {
  ticket: TicketDetail
  messages: MessageItem[]
  events: EventItem[]
  assignees: AssigneeItem[]
}

export interface StaffMember {
  id: string
  username: string
  name: string
  email: string
  gravatar_hash: string | null
}

export interface UserSearchItem {
  id: string
  username: string
  name: string
  email: string
}

// ==========================================================================
// Types — Users
// ==========================================================================

export interface StaffUserListItem {
  id: string
  username: string
  email: string
  email_verified: boolean
  created_at: string
}

export interface StaffUserDetail {
  id: string
  username: string
  email: string
  email_verified: boolean
  created_at: string
  discord_user_id: number | null
  is_staff: boolean
}

export interface StaffUserProject {
  id: string
  name: string
  endpoint_id: string | null
  endpoint_enabled: boolean | null
}

// ==========================================================================
// Types — Projects
// ==========================================================================

export interface StaffProjectBase {
  id: string
  owner_id: string
  username: string
  name: string
  billing_plan: string | null
  subscription_status: string
  visibility: string
  created_at: string
}

export interface StaffProjectListItem extends StaffProjectBase {
  collaborator_count: number
  endpoint_count: number
  issues: string[]
}

/**
 * Current-period meter readings from Stripe for one project, keyed by meter
 * name (e.g. `RelayHours`). `null` when there's no Stripe subscription or the
 * Stripe call failed; the backend logs the failure so a missing reading is
 * distinguishable from a true zero.
 */
export type StaffProjectUsage = Record<string, number | null>

/** Usage per project id; ids the backend doesn't know are absent. */
export type StaffProjectsUsage = Record<string, StaffProjectUsage>

export interface StaffPlanMigration {
  id: string
  current_plan: string | null
  target_plan: string
  status: PlanMigrationStatus
  created_at: string
  resolved_at: string | null
}

export interface LegacyPriceItem {
  item_id: string
  current_price_id: string
  target_price_id: string
  label: string
}

/** Staff override for a project's enforced spend-cap-reached state. */
export type SpendCapOverride = "auto" | "reached" | "relieved"

export interface StaffProjectDetail extends StaffProjectBase {
  owner_id: string
  billing_name: string
  billing_email: string
  stripe_customer_id: string
  stripe_subscription_id: string | null
  description: string
  updated_at: string
  spend_cap_reached: boolean
  spend_cap_override: SpendCapOverride
  pending_migration: StaffPlanMigration | null
  legacy_price_items: LegacyPriceItem[]
  legacy_price_items_error: string | null
  /** Whether the plan grants shared-relay limits, derived from `BillingPlan::shared_relay_limits`. */
  grants_shared_relay: boolean
}

export interface StaffProjectStats {
  relay_count: number
  api_key_count: number
  collaborator_count: number
}

export interface StaffCohortMetrics {
  d30_cohort_size: number
  d30_converted_count: number
}

export interface StaffBuildInfo {
  version: string
  git_branch: string
  git_commit: string
  build_date: string
}

export interface MetricInfo {
  active: boolean
  group: string
  metric: string
  label: string
  description: string
  unit: string
  type: string
}

// ==========================================================================
// Types — Deploys
// ==========================================================================

export interface StaffProjectDeploy {
  id: string
  instance_id: string
  deploy_type: string
  status: string
  // Every hostname the deploy serves, canonical one first. Empty until the
  // deploy has a hostname.
  domains: DeployDomainRef[]
  region: string
  orphaned: boolean
  created_at: string
}

// ==========================================================================
// Types — Project Members
// ==========================================================================

export interface StaffProjectMember {
  username: string
  user_id: string
  role: string
  gravatar_hash: string | null
}

interface StaffMembersResponse {
  members: StaffProjectMember[]
  total: number
}

// ==========================================================================
// Types — Upcoming Invoices
// ==========================================================================

export interface UpcomingInvoiceRow {
  project_id: string
  owner_username: string
  project_name: string
  billing_plan: string | null
  subscription_id: string
  /** Unix seconds for the close of the upcoming invoice's period. */
  period_end: number | null
  period_start: number | null
  amount_due_cents: number | null
  currency: string | null
  legacy_items: LegacyPriceItem[]
  /** True when Stripe says no upcoming invoice exists (e.g. trial cancels
   * at end without a payment method). Not a failure — distinct from `error`. */
  no_upcoming_invoice: boolean
  /** Set when Stripe lookup failed for this row. */
  error: string | null
}

export interface UpcomingInvoicesResponse {
  rows: UpcomingInvoiceRow[]
  total: number
}

/** A plan price a subscription carries no item for. The other half of
 * {@link LegacyPriceItem}: that one is an item on a superseded price, this one
 * is a price with no item at all, so its meter bills nothing. */
export interface MissingPriceItem {
  price_id: string
  /** The price's Stripe `nickname`; null when it has none (or does not exist). */
  nickname: string | null
  /** The price does not exist in Stripe yet; blocks the backfill. */
  unprovisioned: boolean
}

/** One subscription's drift from its plan's price list. Doubles as the
 * applied-report row: `swaps` and `additions` are what *would* change in a
 * preview and what *did* change in a report. */
export interface PriceBackfillRow {
  project_id: string
  owner_username: string
  project_name: string
  billing_plan: string
  subscription_id: string
  swaps: LegacyPriceItem[]
  additions: MissingPriceItem[]
  /** Redundant items to delete: both they and the item that supersedes them
   * bill the same meter, so it bills twice until they are removed. */
  removals: LegacyPriceItem[]
}

export interface PriceBackfillPreview {
  /** Only subscriptions with something to change. */
  rows: PriceBackfillRow[]
  /** Which Stripe dashboard the price ids belong to. */
  stripe_env: string
  projects_scanned: number
  swaps_total: number
  additions_total: number
  /** Redundant items across every row, each a meter billing twice. */
  removals_total: number
  /** Plan prices not yet created in Stripe. Non-empty blocks the apply. */
  unprovisioned: string[]
}

/** What the backfill did to one subscription: the row, flattened, plus the
 * result of re-reading it from Stripe. */
export interface PriceBackfillAppliedRow extends PriceBackfillRow {
  /** Whether re-reading it from Stripe confirms it now matches its plan. */
  verified: boolean
  error: string | null
}

/** How far the shared-relay rollout has got. Database-derived, safe to poll. */
export interface EnrollmentStatus {
  /** Paid-plan projects the enroll button would still add. */
  pending: number
  /** Projects holding the entitlement. */
  enrolled: number
  /** Of those, with at least one fleet assignment. */
  assigned: number
  /** Of those, whose assigned relay can actually serve them. */
  serving: number
  regions_without_default_relay: RegionName[]
}

export interface PriceBackfillReport {
  rows: PriceBackfillAppliedRow[]
  projects_changed: number
  swaps_applied: number
  additions_applied: number
  /** Redundant items deleted, each a meter that stops billing twice. */
  removals_applied: number
  failures: number
}

// ==========================================================================
// Sort enums (must match backend FromFormField values)
// ==========================================================================

export enum SortDir {
  Asc = "asc",
  Desc = "desc",
}

export enum TicketSortColumn {
  TicketNumber = "ticket_number",
  Priority = "priority",
  UpdatedAt = "updated_at",
  CreatedAt = "created_at",
}

export enum UserSortColumn {
  Username = "username",
  Email = "email",
  EmailVerified = "email_verified",
  CreatedAt = "created_at",
}

export enum ProjectSortColumn {
  Name = "name",
  BillingPlan = "billing_plan",
  SubscriptionStatus = "subscription_status",
  CreatedAt = "created_at",
  CollaboratorCount = "collaborator_count",
  EndpointCount = "endpoint_count",
}

// ==========================================================================
// Query params
// ==========================================================================

interface ListTicketsParams {
  status?: SupportTicketStatus
  exclude_status?: SupportTicketStatus
  assignee?: string
  sort_by?: TicketSortColumn
  sort_dir?: SortDir
  offset?: number
  limit?: number
}

// ==========================================================================
// Cache tags
// ==========================================================================

enum Tag {
  Tickets = "StaffTickets",
  TicketDetail = "StaffTicketDetail",
  Staff = "StaffMembers",
  Users = "StaffUsers",
  Projects = "StaffProjects",
  MetricsConfig = "StaffMetricsConfig",
  Entitlements = "StaffEntitlements",
  Deploys = "StaffDeploys",
  ProjectMembers = "StaffProjectMembers",
  PlanMigrations = "StaffPlanMigrations",
  UpcomingInvoices = "StaffUpcomingInvoices",
  PriceBackfill = "StaffPriceBackfill",
  EnrollmentStatus = "StaffEnrollmentStatus",
  CloudProvider = "StaffCloudProvider",
  AllDeploys = "StaffAllDeploys",
  BaseDomains = "StaffBaseDomains",
  CustomDomains = "StaffCustomDomains",
  WorkflowLog = "StaffWorkflowLog",
  Builds = "StaffBuilds",
  Fleets = "StaffFleets",
  RelaySettings = "StaffRelaySettings",
}

// ==========================================================================
// Types — Builds
// ==========================================================================

export interface StaffBuildsResponse {
  builds: Build[]
  total: number
}

export interface ListStaffBuildsParams {
  offset?: number
  limit?: number
  type?: BinaryType
  includeArchived?: boolean
}

export interface CreateStaffBuildRequest {
  type: BinaryType
  commit_hash: string
  commit_branch: string
  tag: string
  os: string
  arch: string
  variant: string
  binary_url?: string
  binary_hash?: string
}

/** Builds are immutable: the only mutation is archive/unarchive. To change a
 * build, archive the old one and create a new one. */
export interface ArchiveStaffBuildRequest {
  archived: boolean
}

// ==========================================================================
// Types — Infrastructure
// ==========================================================================

export interface CloudProviderToggle {
  cloud_provider: CloudProvider
}

/** Common fields on every active-workflow variant. */
interface ActiveWorkflowBase {
  workflow_id: string
  /** Action type currently running, e.g. `migrate_instance`. */
  step: string | null
  /** Last log line — current human-readable progress beat. */
  progress: string | null
}

/** Active workflow attached to a deploy row. Discriminated by `verb`;
 * create/migrate carry `target_cloud_provider`, update/delete don't. */
export type ActiveWorkflowInfo =
  | (ActiveWorkflowBase & {
      verb: "create"
      target_cloud_provider: CloudProvider
    })
  | (ActiveWorkflowBase & { verb: "update" })
  | (ActiveWorkflowBase & { verb: "delete" })
  | (ActiveWorkflowBase & {
      verb: "migrate"
      target_cloud_provider: CloudProvider
    })
  | (ActiveWorkflowBase & { verb: "running" })

export interface StaffDeployRow {
  deploy_id: string
  instance_id: string
  project_id: string
  project_name: string
  owner_username: string
  cloud_provider: CloudProvider
  region: string
  deploy_type: string
  status: string
  version: string | null
  // Every hostname the deploy serves, canonical one first. Empty until the
  // deploy has a hostname.
  domains: DeployDomainRef[]
  created_at: string
  active_workflow: ActiveWorkflowInfo | null
}

export interface WorkflowLogAction {
  id: string
  index: number
  type: string
  status: string
  log: string
  updated_at: string
}

export interface WorkflowLogEntry {
  id: string
  name: string
  status: string
  created_at: string
  actions: WorkflowLogAction[]
}

// ==========================================================================
// Types — Relay Fleets
// ==========================================================================

export type FleetStatus = "active" | "draining" | "retired"

export interface Fleet {
  id: string
  created_at: string
  updated_at: string
  region: RegionName
  status: FleetStatus
}

/** Global fleet enrollment settings (one singleton row). */
export interface FleetSettings {
  auto_enroll_new_pro: boolean
}

/** One fleet relay VM with its registration + load, from the status endpoint. */
export interface FleetRelay {
  instance_id: string
  /** The member's stable `f…` dns id. */
  member_dns_id: string
  /** Member name `f<id>.{region}.relay.{base}`; null until a primary base domain exists. */
  member_name: string | null
  region: RegionName
  /** Latest relay deploy on the instance; the target for removal. */
  deploy_id: string | null
  deploy_status: string | null
  /** The current deploy's canonical `d…` hostname; changes on replacement. */
  url: string | null
  /** The deployed build's version (`builds.tag`); null when no build is pinned. */
  version: string | null
  registered: boolean
  first_seen_at: string | null
  assigned_tenants: number
  is_default: boolean
}

/** One relay VM's live load, from the per-fleet load endpoint, keyed by deploy_id. */
export interface RelayLoad {
  connections: number
  bandwidth_bps: number
}

/** A tenant on a relay (for the reassign UI). */
export interface RelayTenant {
  project_id: string
  name: string
}

// ==========================================================================
// Types — Staff Relay Settings
// ==========================================================================

/**
 * A per-client rate limit. Both numbers are bytes; `max_burst_bytes` null leaves
 * the relay's default burst.
 */
export interface RateLimit {
  bytes_per_second: number
  max_burst_bytes: number | null
}

/**
 * A rate limit staff can set: the platform default, or one of their choosing.
 *
 * Discriminated on `mode`, matching the backend's tagged enum. `default` is the
 * unset state, so this is never wrapped in a nullable.
 */
export type RateLimitSetting =
  | { mode: "default" }
  | ({ mode: "custom" } & RateLimit)

/**
 * The relay settings staff set for a project, which it cannot set itself.
 *
 * Flat, one field per setting: an override for the project's shared (fleet)
 * relays is the next field to land here.
 */
export interface StaffRelaySettings {
  /** What every dedicated relay the project runs throttles a client to. */
  dedicated_rate_limit: RateLimitSetting
}

/** Result of a staff save: how many of the project's relays applied the push. */
export interface SaveStaffRelaySettingsResponse {
  settings: StaffRelaySettings
  total_relays: number
  applied: number
}

// ==========================================================================
// Types — Domains
// ==========================================================================

/** How the platform manages DNS for a base domain. Tagged by provider so more
 * sources can be added without touching existing ones. */
export type DnsProviderConfig = { provider: "aws"; zone_id: string }

export interface BaseDomain {
  id: string
  domain: string
  is_primary: boolean
  dns_provider_config: DnsProviderConfig | null
  created_at: string
  updated_at: string
}

export interface DeployDomain {
  id: string
  deploy_id: string
  domain: string
  kind: DeployDomainKind
  is_default: boolean
  created_at: string
}

// How a custom-domain change reached the relay: hot-pushed over the relay's live
// backhaul connection (no restart), or applied via a reconfigure workflow (relay
// offline). The workflow handle is opaque here — we don't render it.
export type ApplyOutcome =
  | { kind: "hot_push" }
  | { kind: "reconfigure"; workflow: unknown }

export interface AddCustomDomainResponse {
  domain: DeployDomain
  apply: ApplyOutcome
}

// ==========================================================================
// API
// ==========================================================================

export const staffApi = createApi({
  reducerPath: "staffApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}staff/`,
    mode: "cors",
    credentials: "include",
  }),
  tagTypes: Object.values(Tag),
  endpoints: (build) => ({
    // ------------------------------------------------------------------
    // Support Tickets
    // ------------------------------------------------------------------
    listTickets: build.query<TicketListResponse, ListTicketsParams>({
      query: ({
        status,
        exclude_status,
        assignee,
        sort_by,
        sort_dir,
        offset = 0,
        limit = 50,
      }) => {
        const params = new URLSearchParams()
        if (status) params.set("status", status)
        if (exclude_status) params.set("exclude_status", exclude_status)
        if (assignee) params.set("assignee", assignee)
        if (sort_by) params.set("sort_by", sort_by)
        if (sort_dir) params.set("sort_dir", sort_dir)
        params.set("offset", String(offset))
        params.set("limit", String(limit))
        return `support-tickets?${params.toString()}`
      },
      providesTags: [Tag.Tickets],
    }),
    getTicket: build.query<TicketDetailResponse, number>({
      query: (ticketNumber) => `support-tickets/${ticketNumber}`,
      providesTags: (_result, _error, ticketNumber) => [
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    createTicket: build.mutation<
      { id: string; ticket_number: number; subject: string },
      {
        subject: string
        customer_email: string
        customer_user_id?: string
        priority?: number
        message?: string
        assignee_ids?: string[]
      }
    >({
      query: (body) => ({
        url: "support-tickets",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tag.Tickets],
    }),
    updateTicketStatus: build.mutation<
      void,
      { ticketNumber: number; status: SupportTicketStatus }
    >({
      query: ({ ticketNumber, status }) => ({
        url: `support-tickets/${ticketNumber}/status`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: (_r, _e, { ticketNumber }) => [
        Tag.Tickets,
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    updateTicketPriority: build.mutation<
      void,
      { ticketNumber: number; priority: number }
    >({
      query: ({ ticketNumber, priority }) => ({
        url: `support-tickets/${ticketNumber}/priority`,
        method: "PUT",
        body: { priority },
      }),
      invalidatesTags: (_r, _e, { ticketNumber }) => [
        Tag.Tickets,
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    updateTicketAssignees: build.mutation<
      void,
      { ticketNumber: number; user_ids: string[] }
    >({
      query: ({ ticketNumber, user_ids }) => ({
        url: `support-tickets/${ticketNumber}/assignees`,
        method: "PUT",
        body: { user_ids },
      }),
      invalidatesTags: (_r, _e, { ticketNumber }) => [
        Tag.Tickets,
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    updateTicketCustomer: build.mutation<
      void,
      {
        ticketNumber: number
        customer_user_id: string | null
        customer_email?: string | null
        project_id?: string | null
      }
    >({
      query: ({ ticketNumber, ...body }) => ({
        url: `support-tickets/${ticketNumber}/customer`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { ticketNumber }) => [
        Tag.Tickets,
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    addTicketMessage: build.mutation<
      MessageItem,
      { ticketNumber: number; body: string; internal: boolean }
    >({
      query: ({ ticketNumber, body, internal }) => ({
        url: `support-tickets/${ticketNumber}/messages`,
        method: "POST",
        body: { body, internal },
      }),
      invalidatesTags: (_r, _e, { ticketNumber }) => [
        Tag.Tickets,
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    listStaff: build.query<StaffMember[], void>({
      query: () => "staff",
      providesTags: [Tag.Staff],
    }),
    searchUsers: build.query<UserSearchItem[], string>({
      query: (q) => `users/search?q=${encodeURIComponent(q)}`,
    }),

    // ------------------------------------------------------------------
    // Users
    // ------------------------------------------------------------------
    listUsers: build.query<
      StaffUserListItem[],
      {
        offset?: number
        limit?: number
        q?: string
        sort_by?: UserSortColumn
        sort_dir?: SortDir
      }
    >({
      query: ({ offset = 0, limit = 50, q, sort_by, sort_dir }) => {
        const params = new URLSearchParams()
        params.set("offset", String(offset))
        params.set("limit", String(limit))
        if (q) params.set("q", q)
        if (sort_by) params.set("sort_by", sort_by)
        if (sort_dir) params.set("sort_dir", sort_dir)
        return `users?${params.toString()}`
      },
      providesTags: [Tag.Users],
    }),
    getUser: build.query<StaffUserDetail, string>({
      query: (userId) => `users/${userId}`,
    }),
    getUserProjects: build.query<StaffUserProject[], string>({
      query: (userId) => `users/${userId}/projects`,
    }),
    // ------------------------------------------------------------------
    // Projects
    // ------------------------------------------------------------------
    listProjects: build.query<
      StaffProjectListItem[],
      {
        offset?: number
        limit?: number
        billing_issue?: string
        q?: string
        sort_by?: ProjectSortColumn
        sort_dir?: SortDir
      }
    >({
      query: ({
        offset = 0,
        limit = 50,
        billing_issue,
        q,
        sort_by,
        sort_dir,
      }) => {
        const params = new URLSearchParams()
        params.set("offset", String(offset))
        params.set("limit", String(limit))
        if (billing_issue) params.set("billing_issue", billing_issue)
        if (q) params.set("q", q)
        if (sort_by) params.set("sort_by", sort_by)
        if (sort_dir) params.set("sort_dir", sort_dir)
        return `projects?${params.toString()}`
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: Tag.Projects as const, id: p.id })),
              { type: Tag.Projects, id: "LIST" },
            ]
          : [{ type: Tag.Projects, id: "LIST" }],
    }),
    // Fetched after `listProjects` renders: the readings come from Stripe, one
    // call per project and meter, and would otherwise hold up the whole table.
    getProjectsUsage: build.query<StaffProjectsUsage, string[]>({
      query: (ids) => {
        const params = new URLSearchParams()
        for (const id of ids) params.append("ids", id)
        return `projects/usage?${params.toString()}`
      },
      providesTags: (_result, _error, ids) =>
        ids.map((id) => ({ type: Tag.Projects as const, id })),
    }),
    getProject: build.query<StaffProjectDetail, string>({
      query: (projectId) => `projects/${projectId}`,
      providesTags: (_result, _error, projectId) => [
        { type: Tag.Projects, id: projectId },
      ],
    }),
    getProjectStats: build.query<StaffProjectStats, string>({
      query: (projectId) => `projects/${projectId}/stats`,
      providesTags: (_result, _error, projectId) => [
        { type: Tag.Projects, id: projectId },
      ],
    }),
    getCohortMetrics: build.query<StaffCohortMetrics, void>({
      query: () => "metrics/cohort",
    }),
    getBuildInfo: build.query<StaffBuildInfo, void>({
      query: () => "build-info",
    }),
    getProjectUsage: build.query<
      UsageSummary,
      { projectId: string; start?: number; end?: number }
    >({
      // Omit start/end for the current billing period (subscription anchor);
      // pass both to read a specific historical window.
      query: ({ projectId, start, end }) =>
        start != null && end != null
          ? `projects/${projectId}/usage?start=${start}&end=${end}`
          : `projects/${projectId}/usage`,
    }),
    toggleEndpoint: build.mutation<
      void,
      { projectId: string; isEnabled: boolean }
    >({
      query: ({ projectId, isEnabled }) => ({
        url: `projects/${projectId}/endpoint`,
        method: "PATCH",
        body: { is_enabled: isEnabled },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: Tag.Projects, id: projectId },
        { type: Tag.Projects, id: "LIST" },
      ],
    }),
    setSpendCapOverride: build.mutation<
      void,
      { projectId: string; override: SpendCapOverride }
    >({
      query: ({ projectId, override }) => ({
        url: `projects/${projectId}/spend-cap-override`,
        method: "PATCH",
        body: { override },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: Tag.Projects, id: projectId },
        { type: Tag.Projects, id: "LIST" },
      ],
    }),
    getProjectMetricsConfig: build.query<MetricInfo[], string>({
      query: (projectId) => `projects/${projectId}/metrics_config`,
      providesTags: (_result, _error, projectId) => [
        { type: Tag.MetricsConfig, id: projectId },
      ],
    }),
    putProjectMetricsConfig: build.mutation<
      void,
      { projectId: string; config: MetricInfo[] }
    >({
      query: ({ projectId, config }) => ({
        url: `projects/${projectId}/metrics_config`,
        method: "PUT",
        body: config,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: Tag.MetricsConfig, id: projectId },
      ],
    }),
    getProjectEntitlements: build.query<string[], string>({
      query: (projectId) => `projects/${projectId}/entitlements`,
      providesTags: (_result, _error, projectId) => [
        { type: Tag.Entitlements, id: projectId },
      ],
    }),
    putProjectEntitlements: build.mutation<
      void,
      { projectId: string; entitlements: string[] }
    >({
      query: ({ projectId, entitlements }) => ({
        url: `projects/${projectId}/entitlements`,
        method: "PUT",
        body: entitlements,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: Tag.Entitlements, id: projectId },
      ],
    }),
    getProjectRelaySettings: build.query<StaffRelaySettings, string>({
      query: (projectId) => `projects/${projectId}/relay-settings`,
      providesTags: (_result, _error, projectId) => [
        { type: Tag.RelaySettings, id: projectId },
      ],
    }),
    putProjectRelaySettings: build.mutation<
      SaveStaffRelaySettingsResponse,
      { projectId: string; settings: StaffRelaySettings }
    >({
      query: ({ projectId, settings }) => ({
        url: `projects/${projectId}/relay-settings`,
        method: "PUT",
        body: settings,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: Tag.RelaySettings, id: projectId },
      ],
    }),

    // ------------------------------------------------------------------
    // Upcoming Invoices (Stripe fan-out, paginated)
    // ------------------------------------------------------------------
    listUpcomingInvoices: build.query<
      UpcomingInvoicesResponse,
      { offset?: number; limit?: number }
    >({
      query: ({ offset = 0, limit = 25 }) => {
        const params = new URLSearchParams()
        params.set("offset", String(offset))
        params.set("limit", String(limit))
        return `upcoming-invoices?${params.toString()}`
      },
      providesTags: [Tag.UpcomingInvoices],
    }),
    enrollmentStatus: build.query<EnrollmentStatus, void>({
      query: () => "fleets/enrollment-status",
      providesTags: [Tag.EnrollmentStatus],
    }),
    previewPriceBackfill: build.query<PriceBackfillPreview, void>({
      query: () => "billing/price-backfill",
      providesTags: [Tag.PriceBackfill],
    }),
    applyPriceBackfill: build.mutation<PriceBackfillReport, void>({
      query: () => ({ url: "billing/price-backfill", method: "POST" }),
      invalidatesTags: [Tag.PriceBackfill, Tag.UpcomingInvoices],
    }),
    migrateProjectPrices: build.mutation<{ migrated: number }, string>({
      query: (projectId) => ({
        url: `projects/${projectId}/migrate-prices`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, projectId) => [
        Tag.UpcomingInvoices,
        { type: Tag.Projects, id: projectId },
      ],
    }),

    // ------------------------------------------------------------------
    // Plan Migrations
    // ------------------------------------------------------------------
    createPlanMigration: build.mutation<
      StaffPlanMigration,
      { projectId: string; target_plan: string }
    >({
      query: ({ projectId, target_plan }) => ({
        url: `projects/${projectId}/plan-migration`,
        method: "POST",
        body: { target_plan },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: Tag.Projects, id: projectId },
        { type: Tag.PlanMigrations, id: projectId },
        { type: Tag.Projects, id: "LIST" },
      ],
    }),
    cancelPlanMigration: build.mutation<void, string>({
      query: (projectId) => ({
        url: `projects/${projectId}/plan-migration`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, projectId) => [
        { type: Tag.Projects, id: projectId },
        { type: Tag.PlanMigrations, id: projectId },
        { type: Tag.Projects, id: "LIST" },
      ],
    }),

    // ------------------------------------------------------------------
    // Deploys
    // ------------------------------------------------------------------
    listProjectDeploys: build.query<
      StaffProjectDeploy[],
      { projectId: string; offset?: number; limit?: number }
    >({
      query: ({ projectId, offset = 0, limit = 50 }) =>
        `projects/${projectId}/deploys?offset=${offset}&limit=${limit}`,
      providesTags: (_result, _error, { projectId }) => [
        { type: Tag.Deploys, id: projectId },
      ],
    }),

    deleteProjectDeploy: build.mutation<
      void,
      { projectId: string; deployId: string }
    >({
      query: ({ projectId, deployId }) => ({
        url: `projects/${projectId}/deploys/${deployId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: Tag.Deploys, id: projectId },
      ],
    }),

    // ------------------------------------------------------------------
    // Project Members
    // ------------------------------------------------------------------
    getProjectMembers: build.query<
      StaffMembersResponse,
      { projectId: string; offset?: number; limit?: number }
    >({
      query: ({ projectId, offset = 0, limit = 50 }) =>
        `projects/${projectId}/members?offset=${offset}&limit=${limit}`,
      providesTags: (_result, _error, { projectId }) => [
        { type: Tag.ProjectMembers, id: projectId },
      ],
    }),
    addProjectMember: build.mutation<
      void,
      { projectId: string; username: string; role: string }
    >({
      query: ({ projectId, username, role }) => ({
        url: `projects/${projectId}/members`,
        method: "POST",
        body: { username, role },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: Tag.ProjectMembers, id: projectId },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(projectsApi.util.invalidateTags([SharedTag.MyProjects]))
      },
    }),
    removeProjectMember: build.mutation<
      void,
      { projectId: string; userId: string }
    >({
      query: ({ projectId, userId }) => ({
        url: `projects/${projectId}/members/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: Tag.ProjectMembers, id: projectId },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(projectsApi.util.invalidateTags([SharedTag.MyProjects]))
      },
    }),
    transferProjectOwner: build.mutation<
      void,
      { projectId: string; newOwnerId: string }
    >({
      query: ({ projectId, newOwnerId }) => ({
        url: `projects/${projectId}/transfer-owner`,
        method: "POST",
        body: { new_owner_id: newOwnerId },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: Tag.Projects, id: projectId },
        { type: Tag.ProjectMembers, id: projectId },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(projectsApi.util.invalidateTags([SharedTag.MyProjects]))
      },
    }),

    // ------------------------------------------------------------------
    // Infrastructure
    // ------------------------------------------------------------------
    getCloudProvider: build.query<CloudProviderToggle, void>({
      query: () => "cloud-provider",
      providesTags: [Tag.CloudProvider],
    }),
    setCloudProvider: build.mutation<CloudProviderToggle, CloudProvider>({
      query: (cloud_provider) => ({
        url: "cloud-provider",
        method: "POST",
        body: { cloud_provider },
      }),
      invalidatesTags: [Tag.CloudProvider],
    }),
    listAllDeploys: build.query<StaffDeployRow[], void>({
      query: () => "deploys",
      providesTags: [Tag.AllDeploys],
    }),
    getDeployWorkflowLog: build.query<WorkflowLogEntry[], string>({
      query: (deployId) => `deploys/${deployId}/workflow-log`,
      providesTags: (_result, _error, deployId) => [
        { type: Tag.WorkflowLog, id: deployId },
      ],
    }),
    migrateDeploy: build.mutation<
      unknown,
      { deployId: string; target: CloudProvider }
    >({
      query: ({ deployId, target }) => ({
        url: `deploys/${deployId}/migrate`,
        method: "POST",
        body: { target_cloud_provider: target },
      }),
      invalidatesTags: [Tag.AllDeploys],
    }),
    forceUpdateDeploy: build.mutation<
      unknown,
      { deployId: string; buildId?: string }
    >({
      query: ({ deployId, buildId }) => ({
        url: `deploys/${deployId}/force-update`,
        method: "POST",
        body: { build_id: buildId },
      }),
      invalidatesTags: [Tag.AllDeploys],
    }),

    // ------------------------------------------------------------------
    // Base Domains
    // ------------------------------------------------------------------
    listBaseDomains: build.query<BaseDomain[], void>({
      query: () => "base-domains",
      providesTags: [Tag.BaseDomains],
    }),
    createBaseDomain: build.mutation<
      BaseDomain,
      { domain: string; dns_provider_config?: DnsProviderConfig | null }
    >({
      query: (body) => ({
        url: "base-domains",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tag.BaseDomains],
    }),
    setPrimaryBaseDomain: build.mutation<BaseDomain[], string>({
      query: (id) => ({
        url: `base-domains/${id}/primary`,
        method: "POST",
      }),
      invalidatesTags: [Tag.BaseDomains],
    }),
    setProviderConfigBaseDomain: build.mutation<
      BaseDomain[],
      { id: string; dns_provider_config: DnsProviderConfig | null }
    >({
      query: ({ id, dns_provider_config }) => ({
        url: `base-domains/${id}/provider-config`,
        method: "POST",
        body: { dns_provider_config },
      }),
      invalidatesTags: [Tag.BaseDomains],
    }),
    deleteBaseDomain: build.mutation<unknown, string>({
      query: (id) => ({
        url: `base-domains/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [Tag.BaseDomains],
    }),

    // ------------------------------------------------------------------
    // Custom Domains (per deploy)
    // ------------------------------------------------------------------
    listCustomDomains: build.query<DeployDomain[], string>({
      query: (deployId) => `deploys/${deployId}/custom-domains`,
      providesTags: (_result, _error, deployId) => [
        { type: Tag.CustomDomains, id: deployId },
      ],
    }),
    addCustomDomain: build.mutation<
      AddCustomDomainResponse,
      { deployId: string; domain: string }
    >({
      query: ({ deployId, domain }) => ({
        url: `deploys/${deployId}/custom-domains`,
        method: "POST",
        body: { domain },
      }),
      invalidatesTags: (_r, _e, { deployId }) => [
        { type: Tag.CustomDomains, id: deployId },
      ],
    }),
    setDefaultCustomDomain: build.mutation<
      ApplyOutcome,
      { deployId: string; id: string }
    >({
      query: ({ deployId, id }) => ({
        url: `deploys/${deployId}/custom-domains/${id}/default`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { deployId }) => [
        { type: Tag.CustomDomains, id: deployId },
      ],
    }),
    removeCustomDomain: build.mutation<
      ApplyOutcome,
      { deployId: string; id: string }
    >({
      query: ({ deployId, id }) => ({
        url: `deploys/${deployId}/custom-domains/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { deployId }) => [
        { type: Tag.CustomDomains, id: deployId },
      ],
    }),
    // Builds
    // ------------------------------------------------------------------
    listStaffBuilds: build.query<StaffBuildsResponse, ListStaffBuildsParams>({
      query: ({ offset = 0, limit = 50, type, includeArchived }) => {
        const params = new URLSearchParams({
          offset: String(offset),
          limit: String(limit),
        })
        if (type) params.set("type", type)
        if (includeArchived) params.set("include_archived", "true")
        return `builds?${params}`
      },
      providesTags: [Tag.Builds],
    }),
    createStaffBuild: build.mutation<Build, CreateStaffBuildRequest>({
      query: (body) => ({ url: "builds", method: "POST", body }),
      invalidatesTags: [Tag.Builds],
    }),
    archiveStaffBuild: build.mutation<
      Build,
      { buildId: string } & ArchiveStaffBuildRequest
    >({
      query: ({ buildId, ...body }) => ({
        url: `builds/${buildId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [Tag.Builds],
    }),

    // ------------------------------------------------------------------
    // Relay Fleets
    // ------------------------------------------------------------------
    listFleets: build.query<Fleet[], void>({
      query: () => "fleets",
      providesTags: [{ type: Tag.Fleets, id: "LIST" }],
    }),
    createFleet: build.mutation<Fleet, { region: RegionName }>({
      query: (body) => ({ url: "fleets", method: "POST", body }),
      invalidatesTags: [{ type: Tag.Fleets, id: "LIST" }],
    }),
    // Grant the multitenant_relay entitlement to every paid project not already
    // enrolled, then reconcile. Returns how many were newly enrolled, plus any
    // active-fleet regions with no default relay (their tenants stay unassigned
    // until one exists there).
    enrollAllProProjects: build.mutation<
      { enrolled: number; regions_without_default_relay: RegionName[] },
      void
    >({
      // Enrolls + assigns tenants across every fleet, so invalidate the whole
      // Fleets tag (all per-fleet tenant/load queries), not just the list, so the
      // per-fleet tenant counts refresh from the mutation instead of the 5s poll.
      query: () => ({ url: "fleets/enroll-all-pro", method: "POST" }),
      invalidatesTags: [{ type: Tag.Fleets }, Tag.EnrollmentStatus],
    }),
    getFleetSettings: build.query<FleetSettings, void>({
      query: () => "fleets/settings",
      providesTags: [{ type: Tag.Fleets, id: "SETTINGS" }],
    }),
    setFleetSettings: build.mutation<FleetSettings, FleetSettings>({
      query: (body) => ({ url: "fleets/settings", method: "POST", body }),
      invalidatesTags: [{ type: Tag.Fleets, id: "SETTINGS" }],
    }),
    // Drives the fleet lifecycle machine; the server enforces the legal
    // transitions (active↔draining, draining→retired) and the retire guard.
    setFleetStatus: build.mutation<
      Fleet,
      { fleetId: string; status: FleetStatus }
    >({
      query: ({ fleetId, status }) => ({
        url: `fleets/${fleetId}/status`,
        method: "POST",
        body: { status },
      }),
      invalidatesTags: [{ type: Tag.Fleets, id: "LIST" }],
    }),
    listFleetRelays: build.query<FleetRelay[], string>({
      query: (fleetId) => `fleets/${fleetId}/relays`,
      providesTags: (_r, _e, fleetId) => [{ type: Tag.Fleets, id: fleetId }],
    }),
    spawnFleetRelay: build.mutation<
      Workflow,
      { fleetId: string; buildId?: string }
    >({
      query: ({ fleetId, buildId }) => ({
        url: `fleets/${fleetId}/relays`,
        method: "POST",
        body: { build_id: buildId ?? null },
      }),
      invalidatesTags: (_r, _e, { fleetId }) => [
        { type: Tag.Fleets, id: fleetId },
      ],
    }),
    // Roll a fleet relay to a build in place.
    updateFleetRelay: build.mutation<
      Workflow,
      { fleetId: string; deployId: string; buildId?: string }
    >({
      query: ({ fleetId, deployId, buildId }) => ({
        url: `fleets/${fleetId}/relays/${deployId}/update`,
        method: "POST",
        body: { build_id: buildId ?? null },
      }),
      invalidatesTags: (_r, _e, { fleetId }) => [
        { type: Tag.Fleets, id: fleetId },
      ],
    }),
    // `force` moves the relay's tenants off it (onto another relay in the fleet, or
    // off the fleet when this is the last one) instead of refusing the removal.
    removeFleetRelay: build.mutation<
      Workflow,
      { fleetId: string; deployId: string; force?: boolean }
    >({
      query: ({ fleetId, deployId, force }) => ({
        url: `fleets/${fleetId}/relays/${deployId}${force ? "?force=true" : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { fleetId }) => [
        { type: Tag.Fleets, id: fleetId },
      ],
    }),
    setFleetDefaultRelay: build.mutation<
      void,
      { fleetId: string; relayInstanceId: string }
    >({
      query: ({ fleetId, relayInstanceId }) => ({
        url: `fleets/${fleetId}/default-relay`,
        method: "POST",
        body: { relay_instance_id: relayInstanceId },
      }),
      invalidatesTags: (_r, _e, { fleetId }) => [
        { type: Tag.Fleets, id: fleetId },
      ],
    }),
    fleetLoad: build.query<Record<string, RelayLoad>, string>({
      query: (fleetId) => `fleets/${fleetId}/load`,
      // Polled live AND tagged, so a reassign (which shifts load between relays)
      // invalidates the cached load instead of showing stale numbers for up to
      // one poll interval.
      providesTags: (_r, _e, fleetId) => [{ type: Tag.Fleets, id: fleetId }],
    }),
    listRelayTenants: build.query<
      RelayTenant[],
      { fleetId: string; instanceId: string }
    >({
      query: ({ fleetId, instanceId }) =>
        `fleets/${fleetId}/relays/${instanceId}/tenants`,
      providesTags: (_r, _e, { fleetId }) => [
        { type: Tag.Fleets, id: fleetId },
      ],
    }),
    reassignTenant: build.mutation<
      void,
      { fleetId: string; projectId: string; relayInstanceId: string }
    >({
      query: ({ fleetId, projectId, relayInstanceId }) => ({
        url: `fleets/${fleetId}/assignments/${projectId}`,
        method: "POST",
        body: { relay_instance_id: relayInstanceId },
      }),
      invalidatesTags: (_r, _e, { fleetId }) => [
        { type: Tag.Fleets, id: fleetId },
      ],
    }),
  }),
})

export const {
  // Support Tickets
  useListTicketsQuery,
  useGetTicketQuery,
  useCreateTicketMutation,
  useUpdateTicketStatusMutation,
  useUpdateTicketPriorityMutation,
  useUpdateTicketAssigneesMutation,
  useUpdateTicketCustomerMutation,
  useAddTicketMessageMutation,
  useListStaffQuery,
  useSearchUsersQuery,
  // Users
  useListUsersQuery,
  useGetUserQuery,
  useGetUserProjectsQuery,
  // Projects
  useListProjectsQuery,
  useGetProjectsUsageQuery,
  useGetProjectQuery,
  useGetProjectStatsQuery,
  useGetCohortMetricsQuery,
  useGetBuildInfoQuery,
  useGetProjectUsageQuery,
  useToggleEndpointMutation,
  useSetSpendCapOverrideMutation,
  useGetProjectMetricsConfigQuery,
  usePutProjectMetricsConfigMutation,
  useGetProjectEntitlementsQuery,
  usePutProjectEntitlementsMutation,
  useGetProjectRelaySettingsQuery,
  usePutProjectRelaySettingsMutation,
  // Plan Migrations
  useCreatePlanMigrationMutation,
  useCancelPlanMigrationMutation,
  // Upcoming Invoices / Price Migrations
  useListUpcomingInvoicesQuery,
  useEnrollmentStatusQuery,
  usePreviewPriceBackfillQuery,
  useApplyPriceBackfillMutation,
  useMigrateProjectPricesMutation,
  // Deploys
  useListProjectDeploysQuery,
  useDeleteProjectDeployMutation,
  // Project Members
  useGetProjectMembersQuery,
  useAddProjectMemberMutation,
  useRemoveProjectMemberMutation,
  useTransferProjectOwnerMutation,
  // Infrastructure
  useGetCloudProviderQuery,
  useSetCloudProviderMutation,
  useListAllDeploysQuery,
  useGetDeployWorkflowLogQuery,
  useMigrateDeployMutation,
  useForceUpdateDeployMutation,
  // Base Domains
  useListBaseDomainsQuery,
  useCreateBaseDomainMutation,
  useSetPrimaryBaseDomainMutation,
  useSetProviderConfigBaseDomainMutation,
  useDeleteBaseDomainMutation,
  // Custom Domains
  useListCustomDomainsQuery,
  useAddCustomDomainMutation,
  useSetDefaultCustomDomainMutation,
  useRemoveCustomDomainMutation,
  // Builds
  useListStaffBuildsQuery,
  useCreateStaffBuildMutation,
  useArchiveStaffBuildMutation,
  // Relay Fleets
  useListFleetsQuery,
  useCreateFleetMutation,
  useEnrollAllProProjectsMutation,
  useGetFleetSettingsQuery,
  useSetFleetSettingsMutation,
  useSetFleetStatusMutation,
  useListFleetRelaysQuery,
  useSpawnFleetRelayMutation,
  useUpdateFleetRelayMutation,
  useRemoveFleetRelayMutation,
  useSetFleetDefaultRelayMutation,
  useFleetLoadQuery,
  useListRelayTenantsQuery,
  useReassignTenantMutation,
} = staffApi
